import { motion } from "framer-motion";
import { ShieldCheck, Clock, HeartHandshake } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1765896387387-0538bc9f997e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxjYXJlZ2l2ZXIlMjBzZW5pb3IlMjBzbWlsaW5nJTIwaG9tZXxlbnwwfHx8fDE3NzgzNTczMzV8MA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  return (
    <section
      id="top"
      className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 grain-bg opacity-60 pointer-events-none" aria-hidden />
      <div className="container-pad mx-auto max-w-7xl grid lg:grid-cols-12 gap-10 lg:gap-16 items-center relative">
        <div className="lg:col-span-7">
          <motion.img
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            src="https://customer-assets.emergentagent.com/job_senior-support-pro/artifacts/ajhfd891_IMG_1923.jpg"
            alt="Julicare Health Services logo"
            className="w-40 md:w-52 lg:w-56 h-auto rounded-full mb-7 shadow-[0_10px_30px_rgba(15,41,66,0.10)] ring-1 ring-[#1E5F8C]/10"
            data-testid="hero-logo"
          />
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#2A9D8F] mb-6"
            data-testid="hero-eyebrow"
          >
            Greater Toronto Area Home Healthcare · Since 2022
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#0F2942]"
            data-testid="hero-headline"
          >
            Where Health Meets <em className="text-[#1E5F8C]">Compassion</em>.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-[#475569]"
            data-testid="hero-subhead"
          >
            Julicare Health Services provides professional in-home healthcare
            and personal support designed to help your loved ones live safely,
            comfortably, and independently, at home, where they belong.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <a
              href="#booking"
              className="inline-flex items-center justify-center rounded-full bg-[#1E5F8C] text-[#F8FAFC] px-7 py-4 text-sm md:text-base font-medium hover:bg-[#2A75AB] transition-colors"
              data-testid="hero-cta-book"
            >
              Book a Consultation
            </a>
            <a
              href="#contact"
              className="inline-flex items-center justify-center rounded-full bg-transparent text-[#1E5F8C] border border-[#1E5F8C]/30 hover:border-[#1E5F8C] px-7 py-4 text-sm md:text-base font-medium transition-colors"
              data-testid="hero-cta-contact"
            >
              Contact Us
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-[#475569]"
            data-testid="hero-trust-badges"
          >
            <span className="flex items-center gap-2"><ShieldCheck size={18} className="text-[#7FB3D5]" /> Certified caregivers</span>
            <span className="flex items-center gap-2"><Clock size={18} className="text-[#7FB3D5]" /> 24/7 Support</span>
            <span className="flex items-center gap-2"><HeartHandshake size={18} className="text-[#7FB3D5]" /> PIPEDA-conscious</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative rounded-[2rem] overflow-hidden soft-shadow">
            <img
              src={HERO_IMG}
              alt="Caregiver smiling with elderly patient at home"
              className="w-full h-[440px] md:h-[560px] object-cover"
              data-testid="hero-image"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F2942]/30 via-transparent to-transparent pointer-events-none" />
          </div>

          <div className="absolute -bottom-6 -left-6 md:-left-10 bg-[#F8FAFC] border border-[#1E5F8C]/10 rounded-2xl px-5 py-4 soft-shadow flex items-center gap-3 max-w-[260px]">
            <div className="h-10 w-10 rounded-full bg-[#DCEAF2] flex items-center justify-center">
              <HeartHandshake size={20} className="text-[#1E5F8C]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-[#2A9D8F] font-semibold">Care Promise</p>
              <p className="text-sm text-[#0F2942] leading-snug">Personalised plans, every shift.</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
